import { AuthenticatedSession } from '@/authenticatedSession/authenticatedSession';
import { AuthenticatedSessionContext } from '@/authenticatedSession/AuthenticatedSessionContext';
import { FlattenedMatter } from '@/database/aggregateTypes';
import {
  FirmData,
  LeadSource,
  PracticeArea,
  UserDocument,
} from '@shared/types';
import { useCurrentUser, useFirm, useUsers } from '@/database/hooks/hooks';
import { Loader, Overlay, Title } from '@mantine/core';
import { UseFormReturnType, useForm as useMantineForm } from '@mantine/form';
import { useCallback, useContext, useEffect, useState } from 'react';
import { getAttorneyDefaultHourlyRate } from '@/utils/attorneyDefaultHourlyRate';
import { trimStringsDeep } from '@/utils/trimStringsDeep';
import styles from './AddEditMatterForm.module.css';
import { makeFormData } from './makeFormData';
import { MissingFieldError, saveFormData } from './saveFormData/saveFormData';
import ContactsSection from './subcomponents/ContactsSection/ContactsSection';
import { AddEditMatterFormData } from './types';
import { validateContacts } from './validateContacts';
import MatterDetailsSection from './subcomponents/MatterDetailsSection';
import AddEditMatterFormFooter from './subcomponents/AddEditMatterFormFooter';
import { telemetry } from '@/lib/telemetry';
import { v4 as uuidv4 } from 'uuid';
interface Props {
  matter?: FlattenedMatter;
  initialOverrides?: Partial<AddEditMatterFormData>;
  onClose: () => void;
}

export type MantineForm = UseFormReturnType<
  AddEditMatterFormData,
  (values: AddEditMatterFormData) => AddEditMatterFormData
>;

export type SaveStatus =
  | { status: 'loading' }
  | { status: 'error'; message: string };

const AddEditMatterForm = ({ matter, initialOverrides, onClose }: Props) => {
  const { currentUser } = useCurrentUser();

  // For new matters, defer rendering until the current user's Firestore doc
  // is loaded. The form is initialized once on mount in Mantine's
  // 'uncontrolled' mode, so we need to know upfront whether the user is an
  // attorney to pre-populate the responsible attorney field.
  if (!matter && currentUser === undefined) {
    return (
      <div className={styles.addEditMatterForm}>
        <Title order={1} size='lg' c='grayText'>
          New Matter
        </Title>
        <Loader type='dots' color='pink.4' size='lg' />
      </div>
    );
  }

  return (
    <AddEditMatterFormContent
      matter={matter}
      initialOverrides={initialOverrides}
      onClose={onClose}
      currentUser={currentUser}
    />
  );
};

interface ContentProps extends Props {
  currentUser: UserDocument | undefined;
}

const AddEditMatterFormContent = ({
  matter,
  initialOverrides,
  onClose,
  currentUser,
}: ContentProps) => {
  const authSession = useContext(AuthenticatedSessionContext);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>();
  const { firm } = useFirm();
  const leadSourceOptions = [
    { label: 'None', value: 'none' },
    ...(firm?.leadSources
      ?.filter((source: LeadSource) => !source.deletedTimestamp)
      .map((source: LeadSource) => ({
        label: source.name,
        value: source.id,
      })) ?? []),
  ];

  const practiceAreaOptions = [
    { label: 'None', value: '' },
    ...(firm?.practiceAreas
      ?.filter((area: PracticeArea) => !area.deletedTimestamp)
      .map((area: PracticeArea) => ({
        label: area.name,
        value: area.id,
      })) ?? []),
  ];

  // For new matters, default the responsible attorney and both originator
  // fields to the current user when they are themselves an attorney. Each
  // field is seeded independently, so a caller-provided override still
  // wins on a per-field basis. One-shot at form initialization — manually
  // changing one field later does not propagate to the others.
  //
  // The originator fields live on four of the five fee-type union variants
  // (everything except Insurance), so they don't show up on
  // `Partial<AddEditMatterFormData>` (which exposes only keys common to
  // every variant). Cast through a small structural type to read them.
  // `composeFormData` in `makeFormData.ts` spreads `initialOverrides` last,
  // so these flat overrides land on the final form data regardless of which
  // fee-type branch is chosen.
  //
  // Order matters: the caller's overrides are spread first, then each
  // default is conditionally spread *after*. That way an explicit
  // `responsibleAttorneyId: undefined` from the caller (rare but possible
  // with a `Partial`) does not wipe out the default — the per-field
  // `=== undefined` check still applies and the trailing spread of the
  // default overrides the explicit-undefined value. Mirrors PRA-689.
  const personFieldOverrides = initialOverrides as
    | Partial<{
        responsibleAttorneyId: string | null;
        referralOriginatorId: string | null;
        engagementOriginatorId: string | null;
      }>
    | undefined;
  const effectiveInitialOverrides: Partial<AddEditMatterFormData> | undefined =
    !matter && currentUser?.isAttorney
      ? {
          ...initialOverrides,
          ...(personFieldOverrides?.responsibleAttorneyId === undefined && {
            responsibleAttorneyId: currentUser.id,
          }),
          ...(personFieldOverrides?.referralOriginatorId === undefined && {
            referralOriginatorId: currentUser.id,
          }),
          ...(personFieldOverrides?.engagementOriginatorId === undefined && {
            engagementOriginatorId: currentUser.id,
          }),
        }
      : initialOverrides;

  const mantineForm: MantineForm = useMantineForm<AddEditMatterFormData>({
    mode: 'uncontrolled',
    // Mark a field as touched only on focus (input's built-in `onFocus`
    // handler) rather than on every value change. This keeps the new-matter
    // auto-populate of `responsibleAttyHourlyRate` below from inadvertently
    // marking itself as touched — otherwise the first programmatic
    // `setFieldValue` would flip `isTouched('responsibleAttyHourlyRate')`
    // to `true` and subsequent attorney changes would be skipped by the
    // touched-guard. Requires `@mantine/form` 7.15+ (was silently ignored
    // in 7.13.4, which this workspace was pinned to until bumped to 7.17.x).
    touchTrigger: 'focus',
    initialValues: makeFormData({
      matter,
      leadSourceOptions,
      practiceAreaOptions,
      firm,
      initialOverrides: effectiveInitialOverrides,
    }),
    // Strip leading/trailing whitespace from every string field before the
    // submit callback runs (PRA-191). Most importantly, a trailing space in
    // an email address breaks downstream email delivery — but the same hazard
    // applies to matter names, contact names, scope text, etc. Mantine runs
    // `transformValues` after validation; `validateContacts` trims emails
    // independently so a value like `"user@example.com "` doesn't get
    // rejected before transformation can clean it up.
    transformValues: trimStringsDeep,
    validate: {
      clients: (value) => {
        if (value.length === 0) {
          return 'At least one client is required';
        }
        return validateContacts(value);
      },
      adverseParties: (value) => {
        return validateContacts(value);
      },
      insuranceCases: (value, values) => {
        if (values.feeType === 'Insurance' && value.length === 0) {
          return 'At least one insurance case is required for insurance matters';
        }
        return undefined;
      },
    },
  });

  // On a new hourly matter, sync `responsibleAttyHourlyRate` to the
  // responsible attorney's `defaultHourlyRate`. Runs on mount (covers the
  // common case where `effectiveInitialOverrides` has already seeded the
  // responsible attorney to the current-user attorney) and whenever the
  // responsible attorney or fee type changes. Stops writing once the user
  // focuses the rate input — at that point we treat the value as theirs to
  // own, even if they later swap to a different attorney.
  //
  // If the new attorney has no default rate, the field is cleared rather
  // than left holding a stale number from a previous attorney.
  //
  // `useCallback` keeps the handler identity stable across renders so
  // Mantine's `form.watch` (which re-subscribes when its callback arg
  // changes) doesn't churn its internal subscriber list on every render.
  const users = useUsers();
  const isNewMatter = matter === undefined;
  const syncAttorneyRate = useCallback(() => {
    if (!isNewMatter) return;
    const values = mantineForm.getValues();
    if (values.feeType !== 'Hourly') return;
    if (!values.responsibleAttorneyId) return;
    // Skip the sync only if the user has *both* focused the rate input
    // (Mantine's path-keyed touched state) *and* left a non-empty value
    // there. An empty value stays eligible for auto-fill even when
    // touched — this covers two cases:
    //   * The user focuses the rate input on a new hourly matter,
    //     switches fee type to (say) Flat Fee, then back. The sibling
    //     `handleFeeTypeClick` handler resets the rate value to `''`,
    //     but Mantine's touched state persists across the switch.
    //   * The user types a rate and then backspaces it to empty; the
    //     field is now effectively unset, so we re-fill from the
    //     attorney's default.
    const currentValue = values.responsibleAttyHourlyRate;
    const isFieldEmpty = currentValue == null || currentValue === '';
    if (!isFieldEmpty && mantineForm.isTouched('responsibleAttyHourlyRate')) {
      return;
    }
    const attorneyDefaultRate = getAttorneyDefaultHourlyRate(
      users,
      values.responsibleAttorneyId,
    );
    const nextValue: string | null =
      attorneyDefaultRate === undefined ? null : String(attorneyDefaultRate);
    // Idempotent guard — this handler is triggered by mount AND each of the
    // two `form.watch` callbacks below, and may fire several times in quick
    // succession for a single user action.
    if (currentValue === nextValue) return;
    mantineForm.setFieldValue('responsibleAttyHourlyRate', nextValue);
  }, [isNewMatter, mantineForm, users]);

  // Initial pass once `useUsers()` resolves. After that, the `form.watch`
  // handlers below take over.
  useEffect(() => {
    syncAttorneyRate();
  }, [syncAttorneyRate]);

  // Mantine's uncontrolled form needs `form.watch` to react to field changes;
  // `form.values` is not reactive in uncontrolled mode. Same pattern is used
  // in `NewTimeEntryRow.tsx` (watching `matterId`).
  mantineForm.watch('responsibleAttorneyId', syncAttorneyRate);
  mantineForm.watch('feeType', syncAttorneyRate);

  // Get IDs of already added contacts to exclude from search results
  const getAlreadyAddedContactIds = (): string[] => {
    const existingIds: string[] = [];
    const values = mantineForm.getValues();
    values.clients.forEach((contact) => {
      // Add firestoreId if it exists (for existing humans/organizations)
      if (contact.firestoreId) {
        existingIds.push(contact.firestoreId);
      }
    });
    values.adverseParties.forEach((contact) => {
      if (contact.firestoreId) {
        existingIds.push(contact.firestoreId);
      }
    });
    return existingIds;
  };

  return (
    <div className={styles.addEditMatterForm}>
      <Title order={1} size='lg' c='grayText'>
        {matter ? 'Edit Matter' : 'New Matter'}
      </Title>

      <section className={styles.formAndErrorContainer}>
        <form
          autoComplete='off'
          onSubmit={(event) => {
            mantineForm.onSubmit(async (data) => {
              const onSubmitEventId = uuidv4();

              telemetry.info({
                message: 'form onSubmit: Will call submitForm',
                extra: {
                  matterId: matter?.id ?? 'undefined',
                  matterName: data.matterName ?? 'undefined',
                  matterDescription: data.description ?? 'undefined',
                  matterScope: data.scope ?? 'undefined',
                  onSubmitEventId,
                },
              });
              await submitForm({
                authSession,
                firm,
                matter,
                data,
                onStatusChange: setSaveStatus,
                onClose,
                onSubmitEventId,
              });
              telemetry.info({
                message: 'form onSubmit: Did call submitForm',
                extra: {
                  matterId: matter?.id ?? 'undefined',
                  matterName: data.matterName ?? 'undefined',
                  matterDescription: data.description ?? 'undefined',
                  matterScope: data.scope ?? 'undefined',
                  onSubmitEventId,
                },
              });
            })(event);
          }}
        >
          <ContactsSection
            title='Clients'
            form={mantineForm}
            collectionPath='clients'
            primaryContactIdPath='primaryClientTempId'
            contactRole='client'
            getAlreadyAddedContactIds={getAlreadyAddedContactIds}
            shouldExpandAddPanelOnMount={matter === undefined}
          />

          <ContactsSection
            title='Adverse Parties'
            form={mantineForm}
            collectionPath='adverseParties'
            primaryContactIdPath={undefined}
            contactRole='adverseParty'
            getAlreadyAddedContactIds={getAlreadyAddedContactIds}
            shouldExpandAddPanelOnMount={false}
          />

          <MatterDetailsSection
            mantineForm={mantineForm}
            firm={firm}
            leadSourceOptions={leadSourceOptions}
            practiceAreaOptions={practiceAreaOptions}
          />

          <AddEditMatterFormFooter
            matter={matter}
            saveStatus={saveStatus}
            mantineForm={mantineForm}
            onClose={onClose}
          />
        </form>
        {saveStatus?.status === 'loading' && <Overlay color='#fff' />}
      </section>
    </div>
  );
};

const submitForm = async (props: {
  authSession: AuthenticatedSession | undefined;
  firm: FirmData | undefined;
  matter: FlattenedMatter | undefined;
  data: AddEditMatterFormData;
  onStatusChange: (status: SaveStatus | undefined) => void;
  onClose: () => void;
  onSubmitEventId: string;
}) => {
  const {
    authSession,
    firm,
    matter,
    data,
    onStatusChange,
    onClose,
    onSubmitEventId,
  } = props;
  if (!authSession) {
    console.warn(
      'Attempting to create a matter without an authenticated session',
    );
    return;
  }
  if (!firm) {
    return;
  }

  onStatusChange({ status: 'loading' });
  try {
    telemetry.info({
      message: 'submitForm: Will call saveFormData',
      extra: {
        matterId: matter?.id ?? 'undefined',
        matterName: data.matterName ?? 'undefined',
        matterDescription: data.description ?? 'undefined',
        matterScope: data.scope ?? 'undefined',
        onSubmitEventId,
      },
    });
    await saveFormData({
      authSession,
      firm,
      matter,
      formData: data,
      onSubmitEventId,
    });
    telemetry.info({
      message: 'submitForm: Did call saveFormData successfully',
      extra: {
        matterId: matter?.id ?? 'undefined',
        matterName: data.matterName ?? 'undefined',
        matterDescription: data.description ?? 'undefined',
        matterScope: data.scope ?? 'undefined',
        onSubmitEventId,
      },
    });
    // After the form is submitted, clear the conflicts error and close the popup.
    onStatusChange(undefined);
    onClose();
    const event = new CustomEvent('formSubmitted');
    window.dispatchEvent(event);
  } catch (e) {
    onStatusChange({
      status: 'error',
      message: `Save failed: ${(e as Error).message}`,
    });
    if (!(e instanceof MissingFieldError)) {
      telemetry.error({
        error: e,
        message: `submitForm: Failed to save matter form data`,
        extra: {
          matterId: matter?.id ?? 'undefined',
          matterName: data.matterName ?? 'undefined',
          matterDescription: data.description ?? 'undefined',
          matterScope: data.scope ?? 'undefined',
          onSubmitEventId,
        },
      });
    }
  }
};

export default AddEditMatterForm;
